import {
	Badge, Button, Checkbox, Chip, IconButton, Paper, Table, TableBody,
	TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tooltip,
	Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import { ReactComponent as ExportListSvg } from "assets/exportList.svg";
import { ReactComponent as EmptyIcon } from "assets/icon_location_legend.svg";
import { ReactComponent as NarrowSvg } from "assets/narrow.svg";
import { ReactComponent as SpreadSvg } from "assets/spread.svg";
import { ReactComponent as IconFilterSvg } from "assets/icon_filter.svg";
import { ReactComponent as IconTableSvg } from "assets/icon_table.svg";
import { ReactComponent as WarningSvg } from "assets/icon_warning.svg";
import { ReactComponent as MapAnalysisSvg } from "assets/map_analysis.svg";
import Input from "components/input/input";
import FilterColumns from "containers/location-set-detail/filterColumns/filter-columns";
import FilterLocations from "containers/location-set-detail/filterLocations/filter-locations";
import FilterLocationsMenu from "containers/location-set-detail/filterLocations/filter-locations-menu";
import FilterLocationsChip from "containers/location-set-detail/filterLocations/filter-locations-chip";
import { LeftPanel, RightPanel, zoomLevelMedium, Option } from "helpers/constants";
import { AppAction, AppContext, setLeftPanel } from "helpers/context";
import { MapContext } from "helpers/map";
import { LocationData } from "helpers/models";
import { formatAmount, defaultColumnList, handleFilterLocations } from "helpers/helpers";
import { useContext, useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { downloadItem } from "../download/download-item";
import styles from "./location-set-detail.module.scss";
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import VirtualizedLocationTable from "./table-body-modal/virtualized-location-table"

interface Direction {
	code: "asc" | "desc";
	name: "asc" | "desc";
	[key: string]: "asc" | "desc";
}

let lastAggUnit: string | undefined = undefined;
function LocationSetDetail() {
	const [t] = useTranslation();
	const { map } = useContext(MapContext);
	const [context, dispatch] = useContext(AppContext);
	const locationSets = context.locationSets;
  const locationsColumnList = context.locationsColumnList;
  const locationsSize = context.locationsSize;
  const locationSetLoading = context.multipleLocationsSetLoadings;
	const [rowCount, setRowCount] = useState(0);
	const [data, setData] = useState<any[]>([]);
	const [active, setActive] = useState<string>("code");
	const [direction, setDirection] = useState<Direction>({ code: "asc", name: "asc" });
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [filterLocationsEl, setFilterLocationsEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const filterLocationsOpen = Boolean(filterLocationsEl);
	const [invisible, setInvisible] = useState(true);
	const [isActiveData, setIsActiveData] = useState(undefined);
	const [isMore, setIsMore] = useState(false);
	const [columnList, setColumnList] = useState([...locationsColumnList]);


	let defaultFilterData = JSON.parse(JSON.stringify(context.locationsFilterInfo));
	const [filterInfo, setFilterInfo] = useState<any[]>(JSON.parse(JSON.stringify(context.locationsFilterInfo)));
	let unit = context.currentMultipleLocations?.aggregationUnit || "numberOfLocations";
	const isNumberOfLocations = unit === "numberOfLocations";
	const prevUnit = lastAggUnit;

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleOpenHiddenLocationsModal = () => {
		dispatch({
			type: AppAction.setCurrentHiddenLocationsModal,
			payload: {
				...context.currentHiddenLocationsModal,
				open: true,
			}
		})
	}

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleChangeExpanded = (expanded: any, index: any) => {
		columnList[index].expanded = expanded;
	}

	const handleApply = (Data: any) => {
		dispatch({
			type: AppAction.setLocationColumnList,
			payload: [...Data]
		})
		handleClose();
	};

	const handleFilterLocationsClick = (event: any, data?: any) => {
		setFilterLocationsEl(event.currentTarget);
		setIsActiveData(data);
	};

	const handleFilterLocationsClose = () => {
		setFilterLocationsEl(null);
	};


	const handleFilterLocationsChange = ({
		key,
		value,
		index,
		childIdx
	}: {
		key?: string;
		value: any;
		index: number;
		childIdx?: number;
	}) => {
		let infoArr: any = JSON.parse(JSON.stringify(filterInfo));
		let item: any = infoArr[index];
		if (childIdx !== undefined) {
			// 修改子项
			item.children[childIdx] = (key ? { ...item.children[childIdx], [key]: value } : value);
		} else if (key) {
			// 修改顶层
			infoArr[index] = { ...item, [key]: value };
		}

		dispatch({
			type: AppAction.setLocationsFilterInfo,
			payload: [...infoArr]
		})
	};

	const handleFilterLocationsApply = (filterData: any) => {
		dispatch({
			type: AppAction.setLocationsFilterInfo,
			payload: [...filterData]
		})
	};

	const handleSetTableData = (property?: string, propertyValue?: "asc" | "desc") => {//property 排序字段
		let sortKey = property || active;
		if (property) {

			setActive(sortKey);
			let sortValue: "asc" | "desc" = propertyValue || direction[sortKey] === "asc" ? "desc" : "asc";
			direction[sortKey] = sortValue;
			setDirection({ ...direction });
		}
		let arr = [...context.currentMultipleLocations!.items!];
		if (!arr.length) {
			return;
		}
		arr = handleFilterLocations({
			data: arr,
			unit,
			multipleLocationsSearch: context.multipleLocationsSearch,
			locationsFilterInfo: filterInfo,
		});
		arr.sort((a: any, b: any) => {
			if (!a || !b) {
				return 0;
			}

			const aValue = a?.[sortKey];
			const bValue = b?.[sortKey];

			if (!aValue) {
				return direction[sortKey] === "asc" ? -1 : 1;
			} else if (!bValue) {
				return direction[sortKey] === "asc" ? 1 : -1;
			}

			if (direction[sortKey] === "asc") {
				return String(aValue).localeCompare(String(bValue), 'zh-CN', { numeric: true })
			} else {
				return String(bValue).localeCompare(String(aValue), 'zh-CN', { numeric: true })
			}
		});
		setData([...arr]);
		setRowCount(arr.filter((location: LocationData) => location.checked).length);
	};

	const handleCreateFilterInfoData = () => {
		defaultFilterData = defaultFilterData.map((v: any) => {
			if (v.children) {
				v.children = v.children.map((chilItem: any) => {
					if (chilItem.type === "select") {
						chilItem.options = []
						context.currentMultipleLocations?.items?.forEach((item) => {
							if (item[chilItem.title] && !chilItem.options.includes(item[chilItem.title])) {
								chilItem.options.push(item[chilItem.title])
							}
						})
					} else {
						chilItem.show = Boolean(context.currentMultipleLocations?.items?.find(v => v[chilItem.title]))
					}
					return chilItem
				})
			} else {
				v.options = []
				const items = context.currentMultipleLocations?.items ?? [];
				const len = items.length;
				const thresholds = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
				v.options = thresholds.filter(t => len > t);
			}
			return v
		})
		dispatch({
			type: AppAction.setLocationsFilterInfo,
			payload: [...defaultFilterData]
		})
	}

	const closeEdit = (): void => {
		dispatch({ type: AppAction.setMultipleLocationsEdit, payload: false });
	};

	const focusLocation = (location: LocationData): void => {
		if (context.currentMultipleLocationInfo?.id === location.id) {
			dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
			dispatch({ type: AppAction.setCurrentMultipleLocationInfo, payload: undefined });
		} else {
			dispatch({ type: AppAction.setCurrentMultipleLocationInfo, payload: location });
			dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
			dispatch({ type: AppAction.setSearchLocationInfo, payload: undefined });
			dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Single });
			map.setZoomAndCenter(zoomLevelMedium, [location.longitudeWgs, location.latitude]);
		}
	};

	const toggleLocation = (evt: any, location: any): void => {
		evt.stopPropagation();

		let locations = context.currentMultipleLocations!.items?.map((item: LocationData) => {
			if (item.id === location.id) {
				item.checked = !item.checked;
			}
			return item;
		});

		let multipleLocations = locationSets?.map((locationSet: any) => {
			if (locationSet.id === context.currentMultipleLocations!.id) {
				locationSet.items = context.currentMultipleLocations!.items;
			}
			return locationSet;
		});
		let num: number = data?.filter((location: LocationData) => location.checked).length || 0;

		setRowCount(num);
		dispatch({
			type: AppAction.setCurrentMultipleLocations,
			payload: { ...context.currentMultipleLocations, items: locations },
		});

		dispatch({
			type: AppAction.setLocationSets,
			payload: multipleLocations,
		});
	};

	const patchLocation = (item: any): void => {
		dispatch({
			type: AppAction.setEditMultipleLocation,
			payload: item,
		});

		dispatch({
			type: AppAction.setLeftPanel,
			payload: LeftPanel.EditLocation,
		});
	};

	const handleDeleteLocationSet = (location: any) => {
		dispatch({
			type: AppAction.setDeleteLocationModal,
			payload: location,
		});
	};

	const handleChangeLocationsSize = () => {
		dispatch({ type: AppAction.setLocationsSize, payload: !locationsSize });
	};

	const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
		let locations: any = context.currentMultipleLocations!.items?.map((location: any) => {
			data.forEach((item: any) => {
				if (item.id === location.id) {
					location.checked = event.target.checked;
				}
			});
			return location;
		});

		let multipleLocations = locationSets?.map((locationSet: any) => {
			if (locationSet.id === context.currentMultipleLocations!.id) {
				locationSet.items = context.currentMultipleLocations!.items;
			}
			return locationSet;
		});

		dispatch({
			type: AppAction.setLocationSets,
			payload: multipleLocations,
		});

		dispatch({
			type: AppAction.setCurrentMultipleLocations,
			payload: { ...context.currentMultipleLocations, items: locations },
		});

		if (event.target.checked) {
			setRowCount(data.length);
		} else {
			setRowCount(0);
		}
	};

	const handleGetCellValue = (location: any, column: any) => {
		if (column.title === "currency") {
			return context.currentMultipleLocations?.currency
		} if (column.title === "occupancy" && location[column.title]) {
			return t("occupancyOptions." + location[column.title])
		} else if (column.type === "number") {
			return formatAmount(location[column.title])
		} else if (column.title === "name") {
			let inResult = false;
			let locationId = location.id.split("-").join("")
			if (context.option === Option.HAZARD) {
				inResult = !!context.hazardMultipleAnalysedLegends?.every((legendItems) => {
					let layerMap = context.hazardMultipleAnalysisStore?.get(legendItems.layerId);
					return layerMap?.get(locationId)
				})
			} else if (context.option === Option.CLIMATE) {
				inResult = !!context.climateMultipleAnalysedLegends?.every((legendItems) => {
					return context.climateMultipleAnalysisResult?.find((item) => {
						return item.climateRiskScores?.find((itemScores: any) => (item.id?.split("-").join("") === locationId) && (legendItems.id === itemScores.index));
					})
				})
			} else if (context.option === Option.EVENT) {
				inResult = !!context.eventAnalysisLegend?.every((legendItems) => {
					let layerMap = context.eventAnalysisStore?.get(legendItems.layerId);
					return layerMap?.get(locationId)
				})
			}
			return <div style={{ display: "flex", alignItems: "center" }}>
				<span style={{
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					overflow: "hidden",
				}}>{location[column.title]}</span>
				{inResult && <MapAnalysisSvg style={{
					width: "20px",
					height: "20px",
					fill: "#0009",
					margin: "-5px 5px 0",
					flexShrink: 0
				}} />}
			</div>
		} else {
			return location[column.title]
		}
	}

	const downloadLocationList = () => {
		const locationsTable = handleFilterLocations({
			data: context.currentMultipleLocations?.items || [],
			unit: context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations",
			multipleLocationsSearch: context.multipleLocationsSearch,
			locationsFilterInfo: context.locationsFilterInfo,
			filterChecked: true
		})
		// downloadItem(context, locationsTable, context.currentMultipleLocations?.name || "download", t);
		if (context.currentMultipleLocations) {
			downloadItem(locationsTable, context.currentMultipleLocations, t);
		}
	};

	const badgeCount = useMemo(() => {
		let count = 0;
		filterInfo.forEach(item => {
			// 顶层：value 有值就 +1
			if (item.value) count++;

			// 子项：children 可能为空
			item.children?.forEach((child: any) => {
				const hasArrayValue =
					Array.isArray(child.value) && child.value.length > 0;

				const hasRange = (child.type === "time" || child.type === "number") && (child.min || child.max);
				if (hasArrayValue || hasRange) count++;
			});
		});

		return count;
	}, [filterInfo]);

	const visibleColumns = useMemo(
		() =>
			columnList.flatMap((c) => c.children.filter((cc: any) => cc.show)),
		[columnList]
	);

	const tableData = useMemo(() => {
		return data.map((location) => {
			const cells: Record<string, React.ReactNode> = {};

			visibleColumns.forEach((col) => {
				cells[col.title] = handleGetCellValue(location, col);
			});

			return {
				...location,
				__cells: cells,
			};
		});
	}, [
		data,
		visibleColumns,
		context.option,
		context.language,
		context.currentMultipleLocations?.currency,
		context.hazardMultipleAnalysedLegends,
		context.hazardMultipleAnalysisStore,
		context.climateMultipleAnalysedLegends,
		context.climateMultipleAnalysisResult,
		context.eventAnalysisLegend,
		context.eventAnalysisStore,
	]);


	useEffect(() => {
		if (context.currentMultipleLocations?.items && context.currentMultipleLocations.items.length > 0) {
			handleCreateFilterInfoData();
			if (isNumberOfLocations) {
				if (prevUnit && prevUnit !== "numberOfLocations") {
					let newColumnData = JSON.parse(JSON.stringify(columnList));
					newColumnData[2].children = newColumnData[2].children.map((v: any) => {
						if (v.title === prevUnit) {
							v.show = false;
						} else if (prevUnit !== "numberOfRisks" && v.title === "currency") {
							v.show = false;
						}
						return v
					})
					handleApply(JSON.parse(JSON.stringify(newColumnData)))
				}
				handleSetTableData()
			} else {
				let newColumnData = JSON.parse(JSON.stringify(columnList));
				newColumnData[2].children = newColumnData[2].children.map((v: any) => {
					if (prevUnit && prevUnit !== "numberOfLocations" && v.title === prevUnit) {
						v.show = false;
					};
					if (v.title === unit) {
						v.show = true;
					} else if (unit !== "numberOfRisks" && v.title === "currency") {
						v.show = true;
					}
					return v
				})
				handleApply(JSON.parse(JSON.stringify(newColumnData)))
				handleSetTableData(unit, "desc")
			}
		} else {
			setData([]);
			setRowCount(0);
		}
		lastAggUnit = unit;
	}, [context.currentMultipleLocations?.items, unit]);

	useEffect(() => {
		if (context.currentMultipleLocations?.items && context.currentMultipleLocations.items.length > 0) {
			handleSetTableData();
		}
	}, [filterInfo, context.multipleLocationsSearch])

	useEffect(() => {
		if (JSON.stringify(locationsColumnList.map((v: any) => v.children)) === JSON.stringify(defaultColumnList.map((v: any) => v.children))) {
			setInvisible(true);
		} else {
			setInvisible(false);
		}
		setColumnList([...locationsColumnList])
	}, [locationsColumnList])

	useEffect(() => {
		setFilterInfo([...context.locationsFilterInfo])
	}, [context.locationsFilterInfo])

	const scrollParentRef = useRef<HTMLDivElement | null>(null)

	return (
		<div
			className={styles.set}
			style={{ width: locationsSize ? "800px" : "400px" }}
		>
			<div className={styles.header}>
				<div className={styles.title}>
					<div className={styles.titleBox}>
						<IconButton onClick={closeEdit} >
							<span className="material-icons">arrow_back</span>
						</IconButton>
						<div className="flex items-center overflow-hidden">
							<span className={styles.titleText}> {context.currentMultipleLocations?.name}</span>
							<IconButton
								onClick={() => setLeftPanel(dispatch, LeftPanel.EditLocationSet)}
							>
								<span className="material-icons">create</span>
							</IconButton>
							{context.currentHiddenLocationsModal?.tableData.length > 0 &&
								<Tooltip title={t("locations.hiddenTable.tooltip", { num: context.currentHiddenLocationsModal?.tableData.length })}>
									<IconButton
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
										onClick={handleOpenHiddenLocationsModal}
									>
										<WarningSvg
											style={{
												fill: "#ffa02f",
												width: "24px",
												height: "24px",
												flexShrink: 0
											}}
										/>
									</IconButton>
								</Tooltip>}
						</div>
					</div>
					<div className={styles.close}>
						<IconButton onClick={() => setLeftPanel(dispatch, LeftPanel.Empty)} >
							<span className="material-icons">close</span>
						</IconButton>
					</div>
				</div>
				{locationSets && (
					<div className="flex items-center justify-between mx-[10px] mb-[16px]">
						<div className={styles.search}>
							<Input
								icon="search"
								placeholder={t("locationSet.searchPlaceholder")}
								isClose={true}
								onClose={() => {
									dispatch({
										type: AppAction.setMultipleLocationsSearch,
										payload: ""
									})
								}}
								onChange={(e) => {
									dispatch({
										type: AppAction.setMultipleLocationsSearch,
										payload: e.target.value
									})
								}}
								value={context.multipleLocationsSearch}
							/>
						</div>
						<div className={styles.switch}>
							{locationsSize ? (
								<Tooltip title={t("tooltip.collapse")}>
									<IconButton
										onClick={handleChangeLocationsSize}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
									>
										<NarrowSvg style={{ width: "20px", height: "17px" }} />
									</IconButton>
								</Tooltip>
							) : (
								<Tooltip title={t("tooltip.expand")}>
									<IconButton
										onClick={handleChangeLocationsSize}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
									>
										<SpreadSvg style={{ width: "20px", height: "17px" }} />
									</IconButton>
								</Tooltip>
							)}
							<Tooltip title={t("locationSet.columnsTitle")}>
								<Badge
									overlap="circular"
									variant="dot"
									invisible={invisible}
									sx={{
										".MuiBadge-badge": { background: "#ffa02f" },
									}}
								>
									<IconButton
										onClick={handleClick}
										color="success"
										sx={{
											width: "36px",
											height: "36px",
											"&:hover": {
												background: "rgba(0, 0, 0, 0.04)",
											},
										}}
										style={{
											background: open || !invisible ? "#c1e5ca" : "",
										}}
									>
										<IconTableSvg
											style={{
												fill: open || !invisible ? "green" : "",
											}}
											className="focus:outline-none w-[20px] h-[18px]"
										/>
									</IconButton>
								</Badge>
							</Tooltip>
						</div>
					</div>
				)}
			</div>
			<div className={styles.filterLocation}>
				<div className={styles.filterLocationSearch}>
					<Tooltip title={t("locationSet.filterLocations")}>
						<Button
							disableRipple
							variant="text"
							color="success"
							onClick={handleFilterLocationsClick}
							size="small"
							startIcon={
								<Badge
									badgeContent={badgeCount}
									color="success"
									sx={{
										"& .MuiBadge-badge": {
											minWidth: "16px",
											width: "16px",
											height: "16px",
											lineHeight: "16px",
											fontSize: "9px",
										}
									}}
								>
									<IconFilterSvg
										style={{
											fill: "green",
											width: "22px",
											height: "22px",
											padding: "2px"
										}}
									/>
								</Badge>
							}
							sx={{ flex: 1 }}
						>
							{t("locationSet.allFilters")}
						</Button>
					</Tooltip>
					<FormControl sx={{ width: "130px" }}>
						<InputLabel
							color="success"
						>
							{t("locations.add.topLocations")}
						</InputLabel>
						<Select
							name="topLocations"
							value={filterInfo[0].value}
							onChange={
								(event) => handleFilterLocationsChange({
									key: "value",
									value: event.target.value,
									index: 0
								})
							}
							label={t("locations.add.topLocations")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{filterInfo[0]?.options?.map((optionsItm: any) => <MenuItem value={optionsItm} key={optionsItm}>{t("locationSet.filterModal." + optionsItm)}</MenuItem>)}
							<MenuItem value="allLocations" >{t("locationSet.filterModal.allLocations")}</MenuItem>
						</Select>
					</FormControl>
					<FormControl sx={{ width: "120px" }}>
						<InputLabel
							color="success"
						>
							{t("locations.add.occupancy")}
						</InputLabel>
						<Select
							name="occupancy"
							value={filterInfo[1]?.children?.[0]?.value?.length && filterInfo[1]?.children?.[0]?.value?.length > 1 ? "" : filterInfo[1]?.children?.[0]?.value?.[0] ?? ""}
							onChange={
								(event) => handleFilterLocationsChange({
									key: "value",
									value: [event.target.value],
									index: 1,
									childIdx: 0
								})
							}
							label={t("locations.add.occupancy")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{filterInfo[1]?.children?.[0]?.options?.map((optionsItm: any) => <MenuItem value={optionsItm} key={optionsItm}>{t("occupancyOptions." + optionsItm)}</MenuItem>)}
						</Select>
					</FormControl>
					<FilterLocationsMenu
						width="110px"
						data={filterInfo[1]?.children?.[7]}
						onChange={(event: any) => handleFilterLocationsChange({
							value: event,
							index: 1,
							childIdx: 7
						})}
					/>
					<FormControl sx={{ width: "130px" }}>
						<InputLabel
							color="success"
						>
							{t("locations.add.county")}
						</InputLabel>
						<Select
							name="county"
							value={filterInfo[2]?.children?.[2]?.value?.length && filterInfo[2]?.children?.[2]?.value?.length > 1 ? "" : filterInfo[2]?.children?.[2]?.value?.[0] ?? ""}
							onChange={
								(event) => handleFilterLocationsChange({
									key: "value",
									value: [event.target.value],
									index: 2,
									childIdx: 2
								})
							}
							label={t("locations.add.county")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{filterInfo[2]?.children?.[2]?.options?.map((optionsItm: any) => <MenuItem value={optionsItm} key={optionsItm}>{optionsItm}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControl sx={{ width: "126px" }}>
						<InputLabel
							color="success"
						>
							{t("locations.add.province")}
						</InputLabel>
						<Select
							name="province"
							value={filterInfo[2]?.children?.[0]?.value?.length && filterInfo[2]?.children?.[0]?.value?.length > 1 ? "" : filterInfo[2]?.children?.[0]?.value?.[0] ?? ""}
							onChange={
								(event) => handleFilterLocationsChange({
									key: "value",
									value: [event.target.value],
									index: 2,
									childIdx: 0
								})
							}
							label={t("locations.add.province")}
							color="success"
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 350,
									},
								},
							}}
						>
							{filterInfo[2]?.children?.[0]?.options?.map((optionsItm: any) => <MenuItem value={optionsItm} key={optionsItm}>{optionsItm}</MenuItem>)}
						</Select>
					</FormControl>
				</div>
				<div className={styles.filterLocationTime}>
					<FilterLocationsMenu
						width="110px"
						data={filterInfo[1]?.children?.[4]}
						onChange={(event: any) => handleFilterLocationsChange({
							value: event,
							index: 1,
							childIdx: 4
						})}
					/>
					<FilterLocationsMenu
						width="110px"
						data={filterInfo[1]?.children?.[5]}
						onChange={(event: any) => handleFilterLocationsChange({
							value: event,
							index: 1,
							childIdx: 5
						})}
					/>
					<FilterLocationsMenu
						width="110px"
						data={filterInfo[1]?.children?.[6]}
						onChange={(event: any) => handleFilterLocationsChange({
							value: event,
							index: 1,
							childIdx: 6
						})}
					/>
				</div>

			</div>
			{locationSetLoading ? (
				<div className={styles.loading}>
					<div className="loading loading-spinner loading-lg"></div>
					<div>{t("layers.loadingLocations")}</div>
				</div>
			) : (
				<>
					{context.currentMultipleLocations && context.currentMultipleLocations.items?.length ? (
						<Paper
							sx={{ width: "100%", overflow: "hidden", height: "556px", position: "relative", boxShadow: "none", flex: 1, display: "flex", flexDirection: "column" }}
							className={styles.table}
						>
							{badgeCount > 0 && (
								<div className={styles.filterContent}>
									<div
										className={`${styles.tableFilterInfo} ${!isMore ? styles.hiddenChild : ""}`}
									>
										{filterInfo.map((item, index) => {
											if (item.children) {
												return item.children.map((child: any, childIdx: number) => (child.value?.length || child.min || child.max)
													&&
													<FilterLocationsChip
														data={child}
														key={child.title}
														handleDeleteFilter={() => {
															if (child.type === "select") {
																handleFilterLocationsChange({
																	key: "value",
																	value: [],
																	index,
																	childIdx
																});
															} else {
																const nf: any = [...filterInfo];
																const parent = nf[index];
																const children = Array.isArray(parent.children) ? [...parent.children] : [];
																const current = { ...children[childIdx] };
																switch (current.type) {
																	case "number":
																		current.min = "";
																		current.max = "";
																		break;
																	case "time":
																		current.min = null;
																		current.max = null;
																		break;
																	default:
																		break;
																}
																children[childIdx] = current;
																nf[index] = { ...parent, children };
																dispatch({
																	type: AppAction.setLocationsFilterInfo,
																	payload: [...nf]
																})
															}

														}}
														handleFilterLocationsClick={
															(event: any) => handleFilterLocationsClick(event, { index: index, childIdx })
														}
													/>)
											} else {
												return item.value &&
													<FilterLocationsChip
														data={item}
														key={item.title}
														handleDeleteFilter={() => {
															handleFilterLocationsChange({
																key: "value",
																value: "",
																index: index
															})
														}}
														handleFilterLocationsClick={
															(event: any) => handleFilterLocationsClick(event, { index: index })
														}
													/>
											}
										})}
										<span>
											{!isMore && badgeCount > 1 &&
												<Chip
													label={t("locations.dataMore", { data: badgeCount - 1 })}
													onClick={() => setIsMore(true)}
												/>}
										</span>
									</div>
									{badgeCount > 1 && <IconButton
										sx={{
											padding: 0,
											height: "24px",
											marginTop: "17px",
											marginRight: "16px"
										}}
										onClick={() => setIsMore(!isMore)}
									>
										<span className="material-icons">{isMore ? "unfold_less" : "unfold_more"}</span>
									</IconButton>}
								</div>
							)}
							<TableContainer sx={{
								height: "100%",
								overflow: 'hidden',
								flex: 1,
								"& [data-overlayscrollbars-viewport]": {
									display: "flex",
									flexDirection: "column"
								}
							}}>
								<OverlayScrollbarsComponent
									style={{ height: '100%' }}      // 占满父容器
									options={{
										overflow: { x: 'scroll', y: 'scroll' }, // 明确设置滚动方向
										scrollbars: {
											autoHide: 'leave',        // 鼠标移动/悬停时显示
											autoHideDelay: 0,        // 需要更“跟手”就设 0；默认有延迟
											theme: 'os-theme-dark'   // 可选：主题（或自定义 CSS）
										}
									}}
									events={{
										initialized: (instance: any) => {
											scrollParentRef.current = instance.elements().viewport
										}
									}}
								>
									<div style={{ flex: 1 }}>
										<VirtualizedLocationTable
											scrollParentRef={scrollParentRef}
											data={tableData}
											columnList={columnList}
											active={active}
											direction={direction}
											rowCount={rowCount}
											onSort={handleSetTableData}
											onToggle={toggleLocation}
											onSelectAll={handleSelectAllClick}
											onRowClick={focusLocation}
											onEdit={patchLocation}
											onDelete={handleDeleteLocationSet}
										/>
									</div>
									<div
										className={styles.pagination}
									>
										{t("locations.table.pagination")}
										{data.length} {t("locations.table.of")} {context.currentMultipleLocations?.itemCount || "0"}
									</div>
								</OverlayScrollbarsComponent>
							</TableContainer>
						</Paper>
					) : (
						<div className={styles.empty}>
							<EmptyIcon></EmptyIcon>
							<div className="my-[15px]">{t("locations.empty")}</div>
							<div>{t("locations.addEmpty")}</div>
						</div>
					)}
				</>
			)
			}

			{
				open && (
					<FilterColumns
						anchorEl={anchorEl}
						defaultData={[...defaultColumnList]}
						data={[...columnList]}
						handleClose={handleClose}
						handleApply={handleApply}
						handleChangeExpanded={handleChangeExpanded}
					/>
				)
			}

			{
				filterLocationsOpen && (
					<FilterLocations
						anchorEl={filterLocationsEl}
						data={[...filterInfo]}
						isActiveData={isActiveData}
						handleClose={handleFilterLocationsClose}
						handleChange={handleFilterLocationsChange}
						handleApply={handleFilterLocationsApply}
					/>
				)
			}

			<div className={styles.footer}>
				<Button
					variant="outlined"
					color="success"
					style={{ width: "100%" }}
					endIcon={<ExportListSvg style={{ width: "18px", height: "18px", fill: "green" }} />}
					disabled={!rowCount}
					onClick={downloadLocationList}
				>
					{t("buttons.exportList")}
				</Button>
			</div>
		</div >
	);
}

export default LocationSetDetail;


